import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { Todo, CreateTodoDto } from "../../types";
import { CreateTodoResponse, UpdateTodoResponse } from "../../types/responses";
import { generateUUID } from "../../utils/uuidUtils";
import { API_ENDPOINTS, authenticatedFetch } from "../../config/api";

interface TodosState {
  items: Todo[];
  loading: boolean;
  error: string | null;
}

const initialState: TodosState = {
  items: [],
  loading: false,
  error: null,
};

// Async thunk for fetching todos via API
const fetchTodos = createAsyncThunk(
  "todos/fetchTodos",
  async (_, { rejectWithValue }) => {
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.TODOS.GET_ALL);

      if (!response.ok) {
        throw new Error(`Failed to fetch todos: ${response.statusText}`);
      }

      const todos: Todo[] = await response.json();
      return todos;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error"
      );
    }
  }
);

// Async thunk for creating a todo via API
const createTodo = createAsyncThunk(
  "todos/createTodo",
  async (todoData: CreateTodoDto, { rejectWithValue }) => {
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.TODOS.CREATE, {
        method: "POST",
        body: JSON.stringify(todoData),
      });

      if (!response.ok) {
        throw new Error(`Failed to create todo: ${response.statusText}`);
      }

      const result: CreateTodoResponse = await response.json();
      return result;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error"
      );
    }
  }
);

// Async thunk for updating a todo via API
const updateTodoAsync = createAsyncThunk(
  "todos/updateTodoAsync",
  async ({ id, updates }: { id: string; updates: Partial<Todo> }, { rejectWithValue }) => {
    try {
      const response = await authenticatedFetch(`${API_ENDPOINTS.TODOS.UPDATE}/${id}`, {
        method: "PUT",
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        throw new Error(`Failed to update todo: ${response.statusText}`);
      }

      const result: UpdateTodoResponse = await response.json();
      return result; // Returns { todo, dailyBingoAwarded, weeklyBingoAwarded, pointsEarned }
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error"
      );
    }
  }
);

// Async thunk for deleting a todo via API
const deleteTodoAsync = createAsyncThunk(
  "todos/deleteTodoAsync",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.TODOS.DELETE(id), {
        method: "DELETE",
      });

      if (!response.ok) {
        throw new Error(`Failed to delete todo: ${response.statusText}`);
      }

      return id; // Return the ID of the deleted todo
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error"
      );
    }
  }
);

const todosSlice = createSlice({
  name: "todos",
  initialState,
  reducers: {
    addTodo: (state, action: PayloadAction<Omit<Todo, "id" | "createdAt">>) => {
      const newTodo: Todo = {
        ...action.payload,
        id: generateUUID(),
        createdAt: new Date().toISOString(),
      };
      state.items.push(newTodo);
    },
    updateTodo: (
      state,
      action: PayloadAction<{ id: string; updates: Partial<Todo> }>
    ) => {
      const { id, updates } = action.payload;
      const todoIndex = state.items.findIndex((todo) => todo.id === id);
      if (todoIndex !== -1) {
        state.items[todoIndex] = { ...state.items[todoIndex], ...updates };
      }
    },
    deleteTodo: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((todo) => todo.id !== action.payload);
    },
    toggleTodo: (state, action: PayloadAction<string>) => {
      const todo = state.items.find((todo) => todo.id === action.payload);
      if (todo) {
        todo.completed = !todo.completed;
      }
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearTodos: (state) => {
      state.items = [];
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTodos.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTodos.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchTodos.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createTodo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTodo.fulfilled, (state, action) => {
        state.loading = false;
        state.items.push(action.payload); // action.payload is CreateTodoResponse which extends Todo
      })
      .addCase(createTodo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateTodoAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTodoAsync.fulfilled, (state, action) => {
        state.loading = false;
        const result = action.payload;
        const updatedTodo = result.todo;
        const index = state.items.findIndex(todo => todo.id === updatedTodo.id);
        if (index !== -1) {
          state.items[index] = updatedTodo;
        }
      })
      .addCase(updateTodoAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteTodoAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTodoAsync.fulfilled, (state, action) => {
        state.loading = false;
        const deletedId = action.payload;
        state.items = state.items.filter(todo => todo.id !== deletedId);
      })
      .addCase(deleteTodoAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  addTodo,
  updateTodo,
  deleteTodo,
  toggleTodo,
  setLoading,
  setError,
  clearTodos,
} = todosSlice.actions;
export { fetchTodos, createTodo, updateTodoAsync, deleteTodoAsync };
export default todosSlice.reducer;
